import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './login.scss'
import logo from 'src/assets/images/1journey.png'
import art from 'src/assets/images/readingbook.png'
import { useLogin } from 'src/Hooks/AuthHooks/useLogin'

/* --- inline icons (no icon-font dependency) --- */
const MailIcon = () => (
  <svg
    className="jl__lead"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="2.5" y="4.5" width="19" height="15" rx="3" />
    <path d="m3.5 6.5 8.5 6 8.5-6" />
  </svg>
)
const LockIcon = () => (
  <svg
    className="jl__lead"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
    <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
  </svg>
)
const EyeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)
const EyeOffIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10.7 6.7A8.9 8.9 0 0 1 12 6.5c6 0 9.5 5.5 9.5 5.5a15 15 0 0 1-3 3.4M6.5 7.9A15 15 0 0 0 2.5 12s3.5 6.5 9.5 6.5a8.6 8.6 0 0 0 3.8-.9" />
    <path d="m9.9 9.9a3 3 0 0 0 4.2 4.2" />
    <path d="M3 3l18 18" />
  </svg>
)
const ArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)

const LoginForm = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const { handleLogin, isLoading } = useLogin()
  const location = useLocation()
  const navigate = useNavigate()
  const [successMessage, setSuccessMessage] = useState(location.state?.successMessage)

  useEffect(() => {
    const userId = localStorage.getItem('userId')
    const token = localStorage.getItem('token')
    if (userId && token && location.pathname === '/login') {
      navigate('/studentDashboard', { replace: true })
    }
  }, [navigate, location])

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage, {
        autoClose: 5000,
        style: { background: 'lightgreen', color: 'white' },
      })
      setSuccessMessage(null)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [successMessage, navigate, location.pathname])

  const validateEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email.trim())
  }

  const validatePassword = () => {
    return password.trim() !== ''
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateEmail() || !validatePassword()) {
      toast.error('Please enter valid email and password', {
        autoClose: 5000,
        style: { background: 'lightcoral', color: 'white' },
      })
      return
    }

    await handleLogin(email, password)
  }

  return (
    <div className="jl">
      <ToastContainer />
      <div className="jl__shell">
        {/* ---------- Brand / hero ---------- */}
        <aside className="jl__hero jl__hero--art">
          <div className="jl__aurora" aria-hidden="true">
            <span className="jl__orb jl__orb--a" />
            <span className="jl__orb jl__orb--b" />
            <span className="jl__orb jl__orb--c" />
            <span className="jl__beam" />
            <span className="jl__grid" />
          </div>

          <div className="jl__art">
            <span className="jl__art-glow" aria-hidden="true" />
            <img src={art} alt="A student reading" className="jl__art-img" />
            <div className="jl__art-tag">
              <span>1Journey</span>
              <strong>Stay with us</strong>
            </div>
          </div>
        </aside>

        {/* ---------- Form ---------- */}
        <main className="jl__panel">
          <div className="jl__card">
            <img src={logo} alt="1Journey" className="jl__card-logo" />
            <span className="jl__eyebrow">Welcome back</span>
            <h2 className="jl__title">Log in to your account</h2>
            <p className="jl__sub">Pick up right where you left off.</p>

            <form className="jl__form" onSubmit={handleSubmit} noValidate>
              <div className="jl__field">
                <label htmlFor="jl-email">Email</label>
                <div className="jl__control">
                  <MailIcon />
                  <input
                    id="jl-email"
                    type="email"
                    placeholder="you@email.com"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="jl__field">
                <label htmlFor="jl-pass">Password</label>
                <div className="jl__control">
                  <LockIcon />
                  <input
                    id="jl-pass"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="jl__eye"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              <div className="jl__meta">
                <Link to="/forgotpassword" className="jl__link">
                  Forgot password?
                </Link>
              </div>

              <button type="submit" className="jl__btn" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="jl__spinner" aria-hidden="true" /> Signing you in&hellip;
                  </>
                ) : (
                  <>
                    Log in <ArrowIcon />
                  </>
                )}
              </button>
            </form>

            <p className="jl__foot">
              New to 1Journey? <Link to="/studentregister">Sign up</Link>
            </p>
          </div>

          <p className="jl__copy">© {new Date().getFullYear()} 1Journey · Learn, Grow, Achieve.</p>
        </main>
      </div>
    </div>
  )
}

export default LoginForm
